import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogProps,
  Grow,
  IconButton,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import MuiDialogTitle, {
  DialogTitleProps,
} from '@material-ui/core/DialogTitle';
import { CloseIcon, SyncIcon } from 'components/Icons';
import { ScreenSize, useScreenSize } from 'plataforma-fundacao-componentes';
import {
  FormEvent,
  MouseEvent,
  ReactNode,
  createRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { modalsActionsClose } from 'store/modules/ModalsActions';
import { IModalProps } from '../../../../store/modules/ModalsReducer';
import { getElementID, clsx } from '../../../../helpers/utils/HTMLUtils';
import styles from './GenericModal.scss';
import translation from 'locales/translation';

const rootClassName = styles.rootClassName;

const DialogTitle = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(),
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.grey[500],
  },
  closeIcon: {
    width: '20px',
    height: '20px',
    color: styles.primarySicredi,
  },
  refreshButton: {
    padding: '10px',
    color: theme.palette.grey[500],
    '&:disabled svg': {
      color: styles.secondarySicredi,
      fill: styles.secondarySicredi,
    },
  },
  refreshSpan: {
    position: 'absolute',
    right: 52,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  refreshIcon: {
    width: '24px',
    height: '24px',
    color: styles.primarySicredi,
  },
}))((props: any) => {
  const {
    children,
    classes,
    className,
    onClose,
    closeText,
    closeButtonId,
    loading,
    showRefreshButton,
    refreshDisabled,
    onRefresh,
  } = props;
  return (
    <MuiDialogTitle
      disableTypography
      className={clsx([classes.root, className])}
    >
      <Typography variant='h6'>{children}</Typography>
      {showRefreshButton ? (
        <Tooltip title={translation.ResultadoParcialModal.labelAtualizar} arrow>
          <span className={classes.refreshSpan}>
            <IconButton
              onClick={onRefresh}
              className={classes.refreshButton}
              disabled={refreshDisabled}
            >
              <SyncIcon animated={loading} className={classes.refreshIcon} />
            </IconButton>
          </span>
        </Tooltip>
      ) : undefined}
      {typeof onClose === 'function' ? (
        <Tooltip title={closeText} arrow>
          <IconButton
            id={closeButtonId}
            aria-label='Close'
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </Tooltip>
      ) : (
        ''
      )}
    </MuiDialogTitle>
  );
});

export interface IGenericModalProps {
  id?: string;
  onClose?: (event?: MouseEvent<HTMLButtonElement>, key?: string) => void;
  DialogProps?: DialogProps;
  DialogTitleProps?: DialogTitleProps;
  DialogContentProps?: DialogContentProps;
  footerContent?: ReactNode;
  onSubmit?: (evt: FormEvent) => void;
  maxWidth?: string;
}

export interface IGenericModal extends IModalProps {
  open: boolean;
  modalKey?: string;
  genericProps?: IGenericModalProps;
  // disableBackdropClick?: boolean;
  // disableEscapeKeyDown?: boolean;
  loading?: boolean;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
}

declare global {
  interface Window {
    getComputedStyle: (
      elt: Element,
      pseudoElt?: string | null | undefined
    ) => CSSStyleDeclaration;
  }
}

const dialogActionsProps = {
  className: clsx([rootClassName + '-actions']),
};

export default function GenericModal(props: IGenericModal) {
  const dispatch = useDispatch();
  const modalID = useMemo(() => {
    return props.genericProps && props.genericProps.id
      ? props.genericProps.id
      : null;
  }, [props.genericProps]);

  const contentRef = createRef();
  const { rankedSize } = useScreenSize();

  const dialogProps = useMemo(() => {
    return {
      open: props.open ?? true,
      fullWidth: true,
      maxWidth: props.genericProps?.maxWidth ?? 'xl',
      fullScreen:
        rankedSize === ScreenSize.xs && props.genericProps?.maxWidth !== 'xs',
      'aria-labelledby': 'max-width-dialog-title',
      ...(props.genericProps?.DialogProps ?? {}),
      // disableBackdropClick:
      //   typeof this.props.disableBackdropClick === 'boolean'
      //     ? this.props.disableBackdropClick
      //     : true,
      // disableEscapeKeyDown:
      //   typeof this.props.disableEscapeKeyDown === 'boolean'
      //     ? this.props.disableEscapeKeyDown
      //     : true,
      className: clsx([
        rootClassName,
        props.genericProps?.onSubmit ? `${rootClassName}-with-form` : '',
        props.genericProps?.DialogProps?.className ?? '',
      ]),
      onClose: () => {
        dispatch(modalsActionsClose(props.modalKey!));
      },
    } as DialogProps;
  }, [
    dispatch,
    props.genericProps?.DialogProps,
    props.genericProps?.maxWidth,
    props.genericProps?.onSubmit,
    props.modalKey,
    props.open,
    rankedSize,
  ]);

  const getForm = useCallback(
    (children: JSX.Element) => {
      return typeof props.genericProps?.onSubmit === 'function' ? (
        <form onSubmit={props.genericProps.onSubmit}>{children}</form>
      ) : (
        children
      );
    },
    [props.genericProps]
  );

  const dialogTitleClassNames = useMemo(() => {
    return [
      rootClassName + '-title',
      props.genericProps?.DialogTitleProps?.className ?? '',
    ];
  }, [props.genericProps]);

  const allDialogTitleProps = useMemo(() => {
    return {
      ...(props.genericProps?.DialogTitleProps ?? {}),
      closeButtonId: modalID ? getElementID(modalID, 'close') : undefined,
      className: dialogTitleClassNames.join(' '),
      loading: props.loading,
      showRefreshButton: props.showRefreshButton,
      onRefresh: props.onRefresh,
      refreshDisabled: props.refreshDisabled,
    };
  }, [
    modalID,
    dialogTitleClassNames,
    props.genericProps,
    props.loading,
    props.onRefresh,
    props.refreshDisabled,
    props.showRefreshButton,
  ]);

  const dialogContentProps = useMemo(() => {
    return {
      ...(props.genericProps?.DialogContentProps ?? {}),
      className: clsx([
        `${rootClassName}-content`,
        props.genericProps?.footerContent
          ? `${rootClassName}-with-actions`
          : '',
        props.genericProps?.DialogContentProps?.className ?? '',
      ]),
      ref: contentRef,
    };
  }, [contentRef, props.genericProps]);

  return (
    <Dialog
      {...dialogProps}
      TransitionComponent={Grow}
      transitionDuration={250}
      TransitionProps={{ style: { transformOrigin: 'center center' } }}
    >
      {getForm(
        <>
          <DialogTitle
            {...allDialogTitleProps}
            onClose={(evt: MouseEvent<HTMLButtonElement>) => {
              if (typeof props.genericProps?.onClose === 'function') {
                props.genericProps?.onClose(evt, props.modalKey);
              }
            }}
            closeText={'Fechar'}
          >
            {props.title}
            {null}
          </DialogTitle>
          <DialogContent {...dialogContentProps}>{props.content}</DialogContent>
          {props.genericProps?.footerContent ? (
            <DialogActions {...dialogActionsProps}>
              {props.genericProps.footerContent}
            </DialogActions>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
